import { useState, useEffect, useCallback, useRef } from 'react';
import { GitBranch, ExternalLink, Check, X, RefreshCw, Trash2, Plus, Github, Search, ChevronDown, Lock, Globe } from 'lucide-react';
import { apiService } from '../services/api';

interface GitRepo {
  _id: string;
  provider: string;
  owner: string;
  repo: string;
  fullName: string;
  repoUrl: string;
  defaultBranch: string;
  isActive: boolean;
  lastTestedAt?: string;
  lastTestStatus?: string;
  lastTestError?: string;
  hasToken: boolean;
}

interface GitHubRepo {
  fullName: string;
  owner: string;
  name: string;
  private: boolean;
  defaultBranch: string;
  htmlUrl: string;
  description: string | null;
  updatedAt: string;
}

interface GitRepoConnectProps {
  projectId: string;
}

type ConnectMode = 'oauth' | 'pat';

export default function GitRepoConnect({ projectId }: GitRepoConnectProps) {
  const [repos, setRepos] = useState<GitRepo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [error, setError] = useState('');

  // GitHub OAuth state
  const [githubConnected, setGithubConnected] = useState(false);
  const [githubUsername, setGithubUsername] = useState('');
  const [githubAvatar, setGithubAvatar] = useState('');
  const [, setCheckingAuth] = useState(true);
  // GitHub OAuth flow is currently disabled — the dashboard's calls to
  // /git/auth/github/url, /git/auth/github/status, /git/auth/github/repos,
  // and DELETE /git/auth/github all 404 (backend never registered them).
  // Default to PAT until the OAuth routes ship; the mode switcher is
  // hidden below so users don't tap a dead OAuth button.
  const [connectMode, setConnectMode] = useState<ConnectMode>('pat');

  // Repo picker state
  const [githubRepos, setGithubRepos] = useState<GitHubRepo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [showRepoDropdown, setShowRepoDropdown] = useState(false);
  const [branch, setBranch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // PAT form state (fallback)
  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');
  const [token, setToken] = useState('');
  const [patBranch, setPatBranch] = useState('');

  useEffect(() => {
    loadRepos();
    checkGitHubStatus();
  }, [projectId]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowRepoDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for OAuth popup callback and detect popup close
  const popupRef = useRef<Window | null>(null);
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== 'github-oauth-callback') return;
      popupRef.current = null;
      if (event.data.success) {
        checkGitHubStatus();
        setShowForm(true);
      } else {
        setError(event.data.error || 'GitHub authorization failed');
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Poll for popup close as fallback (in case postMessage doesn't fire)
  useEffect(() => {
    const interval = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        popupRef.current = null;
        checkGitHubStatus();
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const checkGitHubStatus = async () => {
    try {
      setCheckingAuth(true);
      const res = await apiService.getGitHubAuthStatus();
      if (res.success && res.data) {
        setGithubConnected(res.data.connected);
        setGithubUsername(res.data.githubUsername || '');
        setGithubAvatar(res.data.avatarUrl || '');
      }
    } catch {
      // GitHub OAuth may not be configured — ignore
    } finally {
      setCheckingAuth(false);
    }
  };

  const loadRepos = async () => {
    try {
      setLoading(true);
      const res = await apiService.getGitRepos(projectId);
      if (res.success && res.data) {
        setRepos(res.data);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const loadGitHubRepos = useCallback(async (search?: string) => {
    try {
      setLoadingRepos(true);
      const res = await apiService.getGitHubUserRepos(search);
      if (res.success && res.data) {
        setGithubRepos(res.data);
      }
    } catch {
      setGithubRepos([]);
    } finally {
      setLoadingRepos(false);
    }
  }, []);

  const handleGitHubLogin = async () => {
    setError('');
    try {
      const res = await apiService.getGitHubAuthUrl();
      if (res.success && res.data?.url) {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        popupRef.current = window.open(
          res.data.url,
          'github-oauth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
        );
      } else {
        setError('Failed to get GitHub authorization URL');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to initiate GitHub login');
    }
  };

  const handleDisconnectGitHub = async () => {
    if (!confirm('Disconnect your GitHub account? Existing repo connections will continue to work.')) return;
    try {
      await apiService.disconnectGitHub();
      setGithubConnected(false);
      setGithubUsername('');
      setGithubAvatar('');
      setGithubRepos([]);
      setSelectedRepo(null);
    } catch {
      // ignore
    }
  };

  const handleOAuthConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRepo) {
      setError('Please select a repository');
      return;
    }

    setConnecting(true);
    setError('');
    try {
      const res = await apiService.connectGitRepo({
        projectId,
        owner: selectedRepo.owner,
        repo: selectedRepo.name,
        defaultBranch: branch || undefined,
      });
      if (res.success) {
        setShowForm(false);
        setSelectedRepo(null);
        setBranch('');
        setRepoSearch('');
        loadRepos();
      } else {
        setError(res.error || 'Failed to connect');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handlePatConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!owner || !repo || !token) {
      setError('Owner, repo, and access token are required');
      return;
    }

    setConnecting(true);
    setError('');
    try {
      const res = await apiService.connectGitRepo({
        projectId,
        owner,
        repo,
        accessToken: token,
        defaultBranch: patBranch || undefined,
      });
      if (res.success) {
        setShowForm(false);
        setOwner('');
        setRepo('');
        setToken('');
        setPatBranch('');
        loadRepos();
      } else {
        setError(res.error || 'Failed to connect');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleTest = async (repoId: string) => {
    setTesting(repoId);
    try {
      await apiService.testGitRepo(repoId);
      loadRepos();
    } catch {
      loadRepos();
    } finally {
      setTesting(null);
    }
  };

  const handleDisconnect = async (repoId: string) => {
    if (!confirm('Disconnect this repository?')) return;
    try {
      await apiService.deleteGitRepo(repoId);
      loadRepos();
    } catch {
      // ignore
    }
  };

  const handleRepoSearchChange = (value: string) => {
    setRepoSearch(value);
    setShowRepoDropdown(true);
    loadGitHubRepos(value || undefined);
  };

  const handleSelectRepo = (repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setRepoSearch(repo.fullName);
    setShowRepoDropdown(false);
    setBranch(repo.defaultBranch);
  };

  const openForm = () => {
    setShowForm(true);
    setError('');
    if (githubConnected) {
      setConnectMode('oauth');
      loadGitHubRepos();
    } else {
      setConnectMode('pat');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <GitBranch className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Git Repository</h3>
        </div>
        {!showForm && (
          <button
            onClick={openForm}
            className="flex items-center px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800"
          >
            <Plus className="h-4 w-4 mr-1" />
            Connect Repository
          </button>
        )}
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Connect a GitHub repository to enable automatic code changes from approved proposals.
      </p>

      {/* Connected repos */}
      {loading ? (
        <div className="text-center py-4 text-sm text-gray-400">Loading...</div>
      ) : repos.length === 0 && !showForm ? (
        <div className="text-center py-8 text-gray-500">
          <GitBranch className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm font-medium text-gray-700">No repository connected yet</p>
          <p className="text-xs mt-1 text-gray-500">Connect a GitHub repo so approved variants can open real PRs against your codebase.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {repos.map(r => (
            <div key={r._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full ${r.lastTestStatus === 'success' ? 'bg-green-500' : r.lastTestStatus === 'failure' ? 'bg-red-500' : 'bg-gray-300'}`} />
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{r.fullName}</span>
                    <a href={r.repoUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                  <div className="flex items-center space-x-2 mt-0.5">
                    <span className="text-xs text-gray-500">Branch: {r.defaultBranch}</span>
                    {r.lastTestStatus === 'success' && (
                      <span className="text-xs text-green-600 flex items-center">
                        <Check className="h-3 w-3 mr-0.5" /> Connected
                      </span>
                    )}
                    {r.lastTestStatus === 'failure' && (
                      <span className="text-xs text-red-600 flex items-center" title={r.lastTestError}>
                        <X className="h-3 w-3 mr-0.5" /> Error
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleTest(r._id)}
                  disabled={testing === r._id}
                  className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  title="Test connection"
                >
                  <RefreshCw className={`h-4 w-4 ${testing === r._id ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => handleDisconnect(r._id)}
                  className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                  title="Disconnect"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Connect form */}
      {showForm && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Connect GitHub Repository</h4>

          {/* Mode tabs — OAuth currently hidden; backend routes haven't shipped.
              When /git/auth/github/* routes land, restore the tabs and switch
              the default in `connectMode` initial state above. */}

          {/* OAuth mode */}
          {connectMode === 'oauth' && (
            <>
              {!githubConnected ? (
                <div className="text-center py-4">
                  <button
                    type="button"
                    onClick={handleGitHubLogin}
                    className="inline-flex items-center px-4 py-2.5 bg-[#24292f] text-white text-sm font-medium rounded-lg hover:bg-[#1b1f23] transition-colors"
                  >
                    <Github className="h-5 w-5 mr-2" />
                    Sign in with GitHub
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    Authorize Sessions to access your repositories
                  </p>
                </div>
              ) : (
                <form onSubmit={handleOAuthConnect}>
                  {/* GitHub account info */}
                  <div className="flex items-center justify-between mb-4 p-2.5 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2.5">
                      {githubAvatar ? (
                        <img src={githubAvatar} alt="" className="h-6 w-6 rounded-full" />
                      ) : (
                        <Github className="h-5 w-5 text-gray-600" />
                      )}
                      <div>
                        <span className="text-sm font-medium text-gray-900">{githubUsername}</span>
                        <span className="text-xs text-green-600 ml-2">Connected</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleDisconnectGitHub}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Disconnect
                    </button>
                  </div>

                  {/* Repo picker */}
                  <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Repository</label>
                    <div ref={dropdownRef} className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={repoSearch}
                          onChange={e => handleRepoSearchChange(e.target.value)}
                          onFocus={() => { setShowRepoDropdown(true); if (githubRepos.length === 0) loadGitHubRepos(); }}
                          placeholder="Search your repositories..."
                          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      </div>

                      {showRepoDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {loadingRepos ? (
                            <div className="px-3 py-4 text-center text-sm text-gray-400">
                              <RefreshCw className="h-4 w-4 animate-spin inline mr-1.5" />
                              Loading repositories...
                            </div>
                          ) : githubRepos.length === 0 ? (
                            <div className="px-3 py-4 text-center text-sm text-gray-400">
                              No repositories found
                            </div>
                          ) : (
                            githubRepos.map(r => (
                              <button
                                key={r.fullName}
                                type="button"
                                onClick={() => handleSelectRepo(r)}
                                className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors ${
                                  selectedRepo?.fullName === r.fullName ? 'bg-primary-50' : ''
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2 min-w-0">
                                    {r.private ? (
                                      <Lock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    ) : (
                                      <Globe className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    )}
                                    <span className="text-sm font-medium text-gray-900 truncate">{r.fullName}</span>
                                  </div>
                                  {selectedRepo?.fullName === r.fullName && (
                                    <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />
                                  )}
                                </div>
                                {r.description && (
                                  <p className="text-xs text-gray-400 mt-0.5 ml-5.5 truncate">{r.description}</p>
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Branch override */}
                  {selectedRepo && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-500 mb-1">Default Branch</label>
                      <input
                        type="text"
                        value={branch}
                        onChange={e => setBranch(e.target.value)}
                        placeholder={selectedRepo.defaultBranch}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  )}

                  {error && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); setError(''); setSelectedRepo(null); setRepoSearch(''); }}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={connecting || !selectedRepo}
                      className="px-4 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                    >
                      {connecting ? 'Connecting...' : 'Connect'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}

          {/* PAT mode (fallback) */}
          {connectMode === 'pat' && (
            <form onSubmit={handlePatConnect}>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Owner / Organization</label>
                  <input
                    type="text"
                    value={owner}
                    onChange={e => setOwner(e.target.value)}
                    placeholder="e.g. acme-corp"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Repository</label>
                  <input
                    type="text"
                    value={repo}
                    onChange={e => setRepo(e.target.value)}
                    placeholder="e.g. my-app"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Personal Access Token</label>
                <input
                  type="password"
                  value={token}
                  onChange={e => setToken(e.target.value)}
                  placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Needs <code>repo</code> scope for private repos, or <code>public_repo</code> for public repos.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">Default Branch (optional)</label>
                <input
                  type="text"
                  value={patBranch}
                  onChange={e => setPatBranch(e.target.value)}
                  placeholder="main"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {error && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setError(''); }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={connecting}
                  className="px-4 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {connecting ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
